import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { LucideIcon } from "lucide-react";

type AnimatedTabIconProps = {
  fallbackIcon: LucideIcon;
  gifSrc?: string;
  lottieData?: unknown;
  durationMs: number;
  playKey: number;
};

export type AnimatedTabIconAsset = {
  gifSrc?: string;
  lottieData?: unknown;
  durationMs: number;
};

export type LottieData = {
  w?: number;
  h?: number;
  ip?: number;
  op?: number;
  assets?: LottieAsset[];
  layers?: LottieLayer[];
};

type LottieAsset = {
  id: string;
  layers?: LottieLayer[];
};

type LottieLayer = {
  ty?: number;
  ind?: number;
  parent?: number;
  refId?: string;
  ip?: number;
  op?: number;
  ks?: LottieTransform;
  shapes?: LottieShape[];
};

type LottieTransform = {
  p?: LottieProperty<number[]>;
  a?: LottieProperty<number[]>;
  s?: LottieProperty<number[]>;
  r?: LottieProperty<number>;
  o?: LottieProperty<number>;
};

type LottieShape = {
  ty?: string;
  it?: LottieShape[];
  ks?: LottieProperty<LottiePathValue | LottiePathValue[]>;
  p?: LottieProperty<number[]>;
  a?: LottieProperty<number[]>;
  s?: LottieProperty<number[] | number>;
  r?: LottieProperty<number>;
  w?: LottieProperty<number>;
  lc?: number;
  lj?: number;
  o?: LottieProperty<number>;
  e?: LottieProperty<number>;
};

type LottieProperty<T> = {
  a?: number;
  k?: T | LottieKeyframe<T>[];
};

type LottieKeyframe<T> = {
  t: number;
  s?: T;
  e?: T;
};

type LottiePathValue = {
  i: number[][];
  o: number[][];
  v: number[][];
  c?: boolean;
};

type ShapeStyle = {
  fill: boolean;
  stroke: boolean;
  strokeWidth: number;
  strokeLinecap: "butt" | "round" | "square";
  strokeLinejoin: "miter" | "round" | "bevel";
  trim?: {
    start: number;
    end: number;
  };
};

export function AnimatedTabIcon({
  fallbackIcon: FallbackIcon,
  gifSrc,
  lottieData,
  durationMs,
  playKey,
}: AnimatedTabIconProps) {
  if (lottieData) {
    return (
      <LottieTabIcon
        data={lottieData as LottieData}
        durationMs={durationMs}
        fallbackIcon={FallbackIcon}
        playKey={playKey}
      />
    );
  }

  if (!gifSrc) {
    return (
      <span className="tp-nav-icon tp-nav-animated-icon" aria-hidden="true">
        <FallbackIcon className="tp-nav-animated-fallback" />
      </span>
    );
  }

  return (
    <GifTabIcon
      durationMs={durationMs}
      fallbackIcon={FallbackIcon}
      gifSrc={gifSrc}
      playKey={playKey}
    />
  );
}

function GifTabIcon({
  durationMs,
  fallbackIcon: FallbackIcon,
  gifSrc,
  playKey,
}: {
  durationMs: number;
  fallbackIcon: LucideIcon;
  gifSrc: string;
  playKey: number;
}) {
  const [stillSrc, setStillSrc] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const timerRef = useRef<number | null>(null);

  const playSrc = useMemo(() => {
    const separator = gifSrc.includes("?") ? "&" : "?";
    return `${gifSrc}${separator}tp_play=${playKey}`;
  }, [gifSrc, playKey]);

  useEffect(() => {
    let cancelled = false;
    const image = new Image();
    image.onload = () => {
      if (cancelled) return;
      const width = image.naturalWidth || image.width;
      const height = image.naturalHeight || image.height;
      if (!width || !height) return;

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext("2d");
      if (!context) return;

      context.drawImage(image, 0, 0, width, height);
      try {
        setStillSrc(canvas.toDataURL("image/png"));
      } catch {
        setStillSrc(null);
      }
    };
    image.src = gifSrc;

    return () => {
      cancelled = true;
    };
  }, [gifSrc]);

  useEffect(() => {
    if (playKey <= 0) return undefined;

    setIsPlaying(true);
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
    }
    timerRef.current = window.setTimeout(() => {
      setIsPlaying(false);
      timerRef.current = null;
    }, durationMs);

    return () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [durationMs, playKey]);

  return (
    <span className="tp-nav-icon tp-nav-animated-icon" aria-hidden="true">
      {isPlaying || stillSrc ? (
        <img
          src={isPlaying ? playSrc : stillSrc ?? gifSrc}
          alt=""
          className="tp-nav-animated-image"
          draggable={false}
        />
      ) : (
        <FallbackIcon className="tp-nav-animated-fallback" />
      )}
    </span>
  );
}

function LottieTabIcon({
  data,
  durationMs,
  fallbackIcon: FallbackIcon,
  playKey,
}: {
  data: LottieData;
  durationMs: number;
  fallbackIcon: LucideIcon;
  playKey: number;
}) {
  const firstFrame = data.ip ?? 0;
  const lastFrame = data.op ?? firstFrame;
  const [frame, setFrame] = useState(firstFrame);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (playKey <= 0) {
      setFrame(firstFrame);
      return undefined;
    }

    const startedAt = performance.now();
    const tick = (now: number) => {
      const progress = Math.min((now - startedAt) / durationMs, 1);
      setFrame(firstFrame + (lastFrame - firstFrame) * progress);
      if (progress < 1) {
        rafRef.current = window.requestAnimationFrame(tick);
      } else {
        setFrame(firstFrame);
        rafRef.current = null;
      }
    };

    rafRef.current = window.requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [durationMs, firstFrame, lastFrame, playKey]);

  const content = useMemo(() => {
    try {
      return renderLottieLayers(data, frame);
    } catch {
      return null;
    }
  }, [data, frame]);

  if (content === null) {
    return (
      <span className="tp-nav-icon tp-nav-animated-icon" aria-hidden="true">
        <FallbackIcon className="tp-nav-animated-fallback" />
      </span>
    );
  }

  return (
    <span className="tp-nav-icon tp-nav-animated-icon" aria-hidden="true">
      <svg
        className="tp-nav-lottie-image"
        viewBox={`0 0 ${data.w ?? 512} ${data.h ?? 512}`}
        focusable="false"
      >
        {content}
      </svg>
    </span>
  );
}

function renderLottieLayers(data: LottieData, frame: number): ReactNode[] {
  const assets = new Map((data.assets ?? []).map((asset) => [asset.id, asset]));
  return renderLayers(data.layers ?? [], frame, assets, "root");
}

function renderLayers(
  layers: LottieLayer[],
  frame: number,
  assets: Map<string, LottieAsset>,
  keyPrefix: string,
): ReactNode[] {
  const byIndex = new Map<number, LottieLayer>();
  for (const layer of layers) {
    if (typeof layer.ind === "number") byIndex.set(layer.ind, layer);
  }

  return layers
    .map((layer, index) =>
      renderLayer(layer, frame, assets, byIndex, `${keyPrefix}-${index}`),
    )
    .filter(Boolean);
}

function renderLayer(
  layer: LottieLayer,
  frame: number,
  assets: Map<string, LottieAsset>,
  byIndex: Map<number, LottieLayer>,
  key: string,
): ReactNode | null {
  if (layer.ip !== undefined && frame < layer.ip) return null;
  if (layer.op !== undefined && frame > layer.op) return null;

  const transform = layerTransformChain(layer, byIndex, frame);
  if (layer.ty === 0 && layer.refId) {
    const asset = assets.get(layer.refId);
    if (!asset?.layers) return null;
    return (
      <g key={key} transform={transform}>
        {renderLayers(asset.layers, frame, assets, key)}
      </g>
    );
  }

  if (layer.ty !== 4 || !layer.shapes) return null;
  return (
    <g key={key} transform={transform}>
      {renderShapeList(layer.shapes, frame, key)}
    </g>
  );
}

function renderShapeList(
  shapes: LottieShape[],
  frame: number,
  keyPrefix: string,
): ReactNode[] {
  return shapes
    .map((shape, index) => renderShape(shape, frame, `${keyPrefix}-shape-${index}`))
    .filter(Boolean);
}

function renderShape(shape: LottieShape, frame: number, key: string): ReactNode | null {
  if (shape.ty !== "gr" || !shape.it) return null;

  const style = groupStyle(shape.it, frame);
  const transform = groupTransform(shape.it, frame);
  const nodes = shape.it
    .map((item, index) => renderDrawable(item, frame, style, `${key}-${index}`))
    .filter(Boolean);

  return (
    <g key={key} transform={transform}>
      {nodes}
    </g>
  );
}

function renderDrawable(
  shape: LottieShape,
  frame: number,
  style: ShapeStyle,
  key: string,
): ReactNode | null {
  if (shape.ty === "gr") return renderShape(shape, frame, key);
  if (shape.ty !== "sh" && shape.ty !== "rc") return null;

  const d = shape.ty === "sh" ? pathForShape(shape, frame) : pathForRect(shape, frame);
  if (!d) return null;

  const trim = style.trim;
  const dashLength = trim ? Math.max(trim.end - trim.start, 0) : 100;
  return (
    <path
      key={key}
      d={d}
      fill={style.fill ? "currentColor" : "none"}
      pathLength={100}
      stroke={style.stroke ? "currentColor" : "none"}
      strokeDasharray={trim ? `${dashLength} ${100 - dashLength}` : undefined}
      strokeDashoffset={trim ? -trim.start : undefined}
      strokeLinecap={style.strokeLinecap}
      strokeLinejoin={style.strokeLinejoin}
      strokeWidth={style.stroke ? style.strokeWidth : undefined}
    />
  );
}

function groupStyle(shapes: LottieShape[], frame: number): ShapeStyle {
  const stroke = shapes.find((shape) => shape.ty === "st");
  const fill = shapes.find((shape) => shape.ty === "fl");
  const trim = shapes.find((shape) => shape.ty === "tm");
  return {
    fill: Boolean(fill),
    stroke: Boolean(stroke),
    strokeWidth: numberValueAt(stroke?.w, frame, 2),
    strokeLinecap: lineCap(stroke?.lc),
    strokeLinejoin: lineJoin(stroke?.lj),
    trim: trim
      ? {
          start: numberValueAt(trim.s, frame, 0),
          end: numberValueAt(trim.e, frame, 100),
        }
      : undefined,
  };
}

function groupTransform(shapes: LottieShape[], frame: number): string | undefined {
  const transform = shapes.find((shape) => shape.ty === "tr");
  return transform ? transformString(transform, frame) : undefined;
}

function layerTransformChain(
  layer: LottieLayer,
  byIndex: Map<number, LottieLayer>,
  frame: number,
): string {
  const transforms: string[] = [];
  let current: LottieLayer | undefined = layer.parent ? byIndex.get(layer.parent) : undefined;
  while (current) {
    const transform = transformString(current.ks, frame);
    if (transform) transforms.unshift(transform);
    current = current.parent ? byIndex.get(current.parent) : undefined;
  }
  const own = transformString(layer.ks, frame);
  if (own) transforms.push(own);
  return transforms.join(" ");
}

function transformString(
  transform: LottieTransform | LottieShape | undefined,
  frame: number,
): string | undefined {
  if (!transform) return undefined;
  const position = vector2(vectorValueAt(transform.p, frame, [0, 0]));
  const anchor = vector2(vectorValueAt(transform.a, frame, [0, 0]));
  const scale = vector2(vectorValueAt(transform.s, frame, [100, 100]));
  const rotation = numberValueAt(transform.r, frame, 0);
  const parts = [
    `translate(${position[0]} ${position[1]})`,
    rotation ? `rotate(${rotation})` : "",
    `scale(${scale[0] / 100} ${scale[1] / 100})`,
    anchor[0] || anchor[1] ? `translate(${-anchor[0]} ${-anchor[1]})` : "",
  ].filter(Boolean);
  return parts.join(" ");
}

function pathForShape(shape: LottieShape, frame: number): string | null {
  const value = valueAt(shape.ks, frame, null);
  const path = Array.isArray(value) ? value[0] : value;
  if (!path || !("v" in path)) return null;
  return pathToD(path);
}

function pathForRect(shape: LottieShape, frame: number): string {
  const [width, height] = vector2(vectorValueAt(shape.s, frame, [0, 0]));
  const [cx, cy] = vector2(vectorValueAt(shape.p, frame, [0, 0]));
  const radius = Math.max(numberValueAt(shape.r, frame, 0), 0);
  const x = cx - width / 2;
  const y = cy - height / 2;
  const right = x + width;
  const bottom = y + height;
  const r = Math.min(radius, width / 2, height / 2);
  if (r <= 0) {
    return `M${x} ${y}H${right}V${bottom}H${x}Z`;
  }
  return [
    `M${x + r} ${y}`,
    `H${right - r}`,
    `Q${right} ${y} ${right} ${y + r}`,
    `V${bottom - r}`,
    `Q${right} ${bottom} ${right - r} ${bottom}`,
    `H${x + r}`,
    `Q${x} ${bottom} ${x} ${bottom - r}`,
    `V${y + r}`,
    `Q${x} ${y} ${x + r} ${y}`,
    "Z",
  ].join("");
}

function pathToD(path: LottiePathValue): string {
  if (path.v.length === 0) return "";
  const commands = [`M${path.v[0][0]} ${path.v[0][1]}`];
  for (let index = 1; index < path.v.length; index += 1) {
    commands.push(curveCommand(path, index - 1, index));
  }
  if (path.c) {
    commands.push(curveCommand(path, path.v.length - 1, 0), "Z");
  }
  return commands.join("");
}

function curveCommand(path: LottiePathValue, fromIndex: number, toIndex: number): string {
  const from = path.v[fromIndex];
  const to = path.v[toIndex];
  const out = path.o[fromIndex] ?? [0, 0];
  const input = path.i[toIndex] ?? [0, 0];
  const c1 = [from[0] + out[0], from[1] + out[1]];
  const c2 = [to[0] + input[0], to[1] + input[1]];
  return `C${c1[0]} ${c1[1]} ${c2[0]} ${c2[1]} ${to[0]} ${to[1]}`;
}

function valueAt<T>(
  prop: LottieProperty<T> | undefined,
  frame: number,
  fallback: T,
): T;
function valueAt<T>(
  prop: LottieProperty<T> | undefined,
  frame: number,
  fallback: T | null,
): T | null;
function valueAt<T>(
  prop: LottieProperty<T> | undefined,
  frame: number,
  fallback: T | null,
): T | null {
  if (!prop || prop.k === undefined) return fallback;
  if (prop.a !== 1 || !isKeyframeArray(prop.k)) return prop.k as T;

  const keyframes = prop.k;
  let current = keyframes[0];
  let next = keyframes[keyframes.length - 1];
  for (let index = 0; index < keyframes.length - 1; index += 1) {
    if (frame >= keyframes[index].t && frame <= keyframes[index + 1].t) {
      current = keyframes[index];
      next = keyframes[index + 1];
      break;
    }
    if (frame > keyframes[index].t) current = keyframes[index];
  }

  const start = current.s ?? fallback;
  const end = current.e ?? next.s ?? start;
  if (start === null || end === null || next.t === current.t) return start;
  const progress = Math.max(Math.min((frame - current.t) / (next.t - current.t), 1), 0);
  return interpolate(start, end, progress) as T;
}

function isKeyframeArray<T>(value: T | LottieKeyframe<T>[]): value is LottieKeyframe<T>[] {
  return Array.isArray(value) && value.length > 0 && typeof value[0] === "object" && "t" in value[0];
}

function numberValueAt(
  prop: LottieProperty<number | number[]> | undefined,
  frame: number,
  fallback: number,
): number {
  const value = valueAt(prop, frame, fallback);
  if (Array.isArray(value) && typeof value[0] === "number") return value[0];
  return typeof value === "number" ? value : fallback;
}

function vectorValueAt(
  prop: LottieProperty<number | number[]> | undefined,
  frame: number,
  fallback: number[],
): number[] {
  const value = valueAt(prop, frame, fallback);
  return Array.isArray(value) ? value : fallback;
}

function interpolate(start: unknown, end: unknown, progress: number): unknown {
  if (typeof start === "number" && typeof end === "number") {
    return start + (end - start) * progress;
  }
  if (Array.isArray(start) && Array.isArray(end)) {
    return start.map((value, index) => interpolate(value, end[index], progress));
  }
  if (isPathValue(start) && isPathValue(end)) {
    return {
      ...start,
      i: interpolate(start.i, end.i, progress) as number[][],
      o: interpolate(start.o, end.o, progress) as number[][],
      v: interpolate(start.v, end.v, progress) as number[][],
    };
  }
  return progress < 1 ? start : end;
}

function isPathValue(value: unknown): value is LottiePathValue {
  return Boolean(
    value &&
      typeof value === "object" &&
      "v" in value &&
      "i" in value &&
      "o" in value,
  );
}

function vector2(value: number[] | null): [number, number] {
  return [value?.[0] ?? 0, value?.[1] ?? 0];
}

function lineCap(value: number | undefined): ShapeStyle["strokeLinecap"] {
  if (value === 2) return "round";
  if (value === 3) return "square";
  return "butt";
}

function lineJoin(value: number | undefined): ShapeStyle["strokeLinejoin"] {
  if (value === 2) return "round";
  if (value === 3) return "bevel";
  return "miter";
}
